import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import zxcvbn from "zxcvbn";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";

function createResetPasswordFormSchema(
  t: (key: Translation, options?: Record<string, unknown>) => string,
) {
  return z
    .object({
      password: z
        .string()
        .min(8, t("resetPassword.form.passwordMinLen", { minLength: 8 }))
        .refine(
          (password) => {
            const { score } = zxcvbn(password);
            return score >= 3;
          },
          {
            message: t("resetPassword.form.weakPassword"),
          },
        ),
      passwordConfirmation: z.string(),
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      message: t("resetPassword.form.passwordDontMatch"),
      path: ["passwordConfirmation"],
    });
}

type ResetPasswordFormValues = z.infer<
  ReturnType<typeof createResetPasswordFormSchema>
>;

export const useResetPasswordForm = () => {
  const t = useTranslation();
  return useForm<ResetPasswordFormValues>({
    resolver: zodResolver(createResetPasswordFormSchema(t)),
    defaultValues: {
      password: "",
      passwordConfirmation: "",
    },
  });
};
